import { useCallback } from 'react';
import { LottieUpdateData } from '../types';
import { currentClip, saveClipChanges, updateClipWithId } from '@/stores/clip';
import { TrackType } from '@/player-config/src/types/Track/Type';
import { ElementsTrack } from '@/player-config/src/types/RemotionPlayerConfigTypes/Track/ElementsTrack';
import { ElementType } from '@/player-config/src/types/Element/Type';
import {
  updatePlayerConfigWithTracks,
  updateClipData,
  generateMotionGraphicName
} from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';
import { getClonedPlayerConfigFromClip } from '@/player-config/src/utils/clipUtils';
import { showErrorToast } from '@/libs/toast/toast';

export const useLottieUpdate = () => {
  const updateLottieElement = useCallback(async (clipId: string, elementId: string, updates: LottieUpdateData) => {
    try {
      const clip = currentClip.getSnapshot()[clipId];
      if (!clip) {
        throw new Error('Clip not found');
      }

      const playerConfig = getClonedPlayerConfigFromClip(clip);
      if (!playerConfig) {
        throw new Error('Player config not found');
      }

      const currentTracks = playerConfig?.playerConfig?.tracks ?? [];

      // Find the ELEMENTS track and update the element directly
      let elementFound = false;
      for (const track of currentTracks) {
        if (track.type === TrackType.ELEMENTS) {
          const elementsTrack = track as ElementsTrack;
          const elementIndex = elementsTrack.elements.findIndex(element => element.id === elementId);

          if (elementIndex !== -1) {
            const targetElement = elementsTrack.elements[elementIndex];

            // Verify it's a lottie element before updating
            if (targetElement.type !== ElementType.LOTTIE) {
              throw new Error('Element is not a Lottie element');
            }

            // Update the specific lottie element
            elementsTrack.elements[elementIndex] = {
              ...targetElement,
              lottie_name: updates.lottie_name,
              hex_colors: updates.hex_colors,
              name: generateMotionGraphicName(updates.lottie_name)
            } as any;
            elementFound = true;
            break;
          }
        }
      }

      if (!elementFound) {
        throw new Error('Lottie element not found');
      }

      // Use common utility functions to update the clip
      const updatedPlayerConfig = updatePlayerConfigWithTracks(playerConfig, currentTracks as ElementsTrack[]);
      const updatedClipData = updateClipData(clip, updatedPlayerConfig);

      updateClipWithId(clipId, updatedClipData);
      saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });
    } catch (error) {
      console.error('Failed to update lottie element:', error);
      showErrorToast('Failed to update Lottie element');
      throw error;
    }
  }, []);

  return {
    updateLottieElement
  };
};
